import React, { memo } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router-dom";
import {
  Icon,
  LoadingIndicator,
  NoData,
  Time
} from "../../../../../../components";
import Grid from "../../../../../../components/Grid";
import { SECTION_PATH } from "../../../../../../path";
import { useLocalSampleSummaryParams } from "../../../../hooks";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { UploadSourceData } from "../../../sources/@types/UploadSource";
import { getUploadAnchorId } from "../../../sources/uploadUtils";
import styles from "./connectors.module.scss";

const ConnectorsErrorComp = () => (
  <span className={commonStyles.error_msg}>Connectors Error</span>
);
const ConnectorsEmptyGridComp = () => (
  <span className={commonStyles.error_msg}>No available Connectors</span>
);

const getPlatformUrl = (item: UploadSourceData) => {
  const metadata = item.upload_metadata;

  // The "Open in Platform" link. Prefer the EDR event URL (falcon, cortex, sentinelone,
  // msdefender, ...) and fall back to the file origin link (e.g. servicenow) when it's missing.
  return metadata.additional_data?.edrEventUrl || metadata.file_origin_link;
};

const getConnectorName = (item: UploadSourceData) =>
  item.upload_metadata?.additional_data?.edrSource ||
  (item.user || "").replace(/_connector$/, "");

const connectorsSchema = (uploadsPath: string) => ({
  fields: [
    {
      name: "record_time",
      displayTitle: "Record Time",
      sortable: false,
      component: (item: UploadSourceData) => (
        <Time time={item.submission_time} />
      ),
      style: {
        width: "15%"
      }
    },
    {
      name: "vendor",
      displayTitle: "Vendor",
      sortable: false,
      component: (item: UploadSourceData) =>
        getConnectorName(item) || <NoData />,
      style: {
        width: "15%"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "file_name_or_hash",
      displayTitle: "File Name / Hash",
      sortable: false,
      component: (item: UploadSourceData) => item.file_name || <NoData />,
      style: {
        width: "40%"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "open_in_platform",
      displayTitle: "Open in Platform",
      sortable: false,
      component: (item: UploadSourceData) => {
        const platformUrl = getPlatformUrl(item);
        if (!platformUrl) return <NoData />;

        return (
          <a
            className={styles.link}
            href={platformUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="connectors_open_in_platform_link"
          >
            <span>{`View in ${getConnectorName(item)}`}</span>
            <Icon className={styles.icon} name="open-in-new" />
          </a>
        );
      },
      style: {
        width: "15%"
      }
    },
    {
      name: "details",
      displayTitle: "",
      sortable: false,
      component: (item: UploadSourceData) => {
        const anchorId = getUploadAnchorId(item.submission_time);
        // ServiceNow "detect" rows have no backing upload to link to → greyed-out disabled label.
        if (!item.has_details || !anchorId) {
          return (
            <span
              className={styles.disabledLink}
              data-testid="connectors_details_disabled"
            >
              Details
            </span>
          );
        }

        return (
          <Link
            className={styles.link}
            to={`${uploadsPath}#${anchorId}`}
            data-testid="connectors_details_link"
          >
            Details
          </Link>
        );
      },
      style: {
        width: "15%",
        textAlign: "right"
      }
    }
  ]
});

interface Props {
  data: UploadSourceData[] | undefined;
  isLoading: boolean;
  isError?: boolean;
  hidePaging: boolean;
}

const ConnectorsGrid: React.FC<Props> = ({
  data,
  isLoading,
  isError,
  hidePaging
}) => {
  const { hash, sourceId } = useLocalSampleSummaryParams();
  // Absolute path to the Uploads section, so the Details deep-link resolves the same regardless of
  // where the grid is rendered (report-summary card vs. the Sources → Connectors tab). A relative
  // path would append to the current route and 404 on the tab.
  const uploadsPath = `/${hash}/source/${sourceId}/${SECTION_PATH.SOURCES_UPLOADS}`;

  return (
    <div data-testid={"connectors-grid"}>
      <ErrorBoundary fallback={<ConnectorsErrorComp />}>
        <Grid
          data={data || []}
          hidePaging={hidePaging}
          enablePageSizeSelector
          headerCellClassName={styles.headerCell}
          schema={connectorsSchema(uploadsPath)}
          showLoading={isLoading}
          emptyGridMsg={
            isError ? <ConnectorsErrorComp /> : <ConnectorsEmptyGridComp />
          }
          showPageNumbers
          LoadingComponent={<LoadingIndicator />}
        />
      </ErrorBoundary>
    </div>
  );
};

export default memo(ConnectorsGrid);
